import React, { useState, useRef, useEffect } from "react";
import { Plus, X, Calendar as CalendarIcon, Clock as ClockIcon, Flag, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { DatePicker, TimePicker } from "@/components/DateTimePicker";
import { todayStr } from "@/utils";

/**
 * Manual task capture — bypasses the AI entirely. The "+" pill sits next to the search bar
 * and opens a compact popover: text · date · time · priority. Hits `POST /api/tasks` directly
 * so the AI can never re-interpret the user's intent (no "let me draft that letter for you"
 * derailments).
 *
 * Props:
 *   - onCreated(task)  called with the created task so the parent can splice it into state
 *
 * Defaults:
 *   - Priority = "med" (matches the app-wide default)
 *   - Type is auto-picked from the presence of date/time (scheduled if either is set,
 *     otherwise todo). Recurring is intentionally OUT — the editor handles that after
 *     the task exists; keeping the quick-add flow narrow is the whole point.
 */
export const ManualTaskButton = ({ onCreated }) => {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState("");
    const [description, setDescription] = useState("");
    const [date, setDate] = useState("");
    const [time, setTime] = useState("");
    const [priority, setPriority] = useState("med");
    // Recurring toggle — off by default (todo/scheduled). When on, the payload becomes
    // type="recurring" with an interval and the entered date acts as the anchor.
    const [isRecurring, setIsRecurring] = useState(false);
    const [interval, setIntervalVal] = useState(7);  // default weekly — matches TaskEditor
    const [busy, setBusy] = useState(false);
    const textRef = useRef(null);
    const popRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        // Focus the text input the moment the popover opens so the user can just start typing.
        // Slight delay lets the mount animation settle so the caret doesn't jump around.
        const id = setTimeout(() => textRef.current?.focus(), 40);
        return () => clearTimeout(id);
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        // Dismiss on outside-click / Escape. Doesn't discard the text — reopening restores it.
        const onDoc = (e) => {
            if (!popRef.current) return;
            if (!popRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const requestIdRef = useRef(null);
    const reset = () => {
        requestIdRef.current = null;
        setText("");
        setDescription("");
        setDate("");
        setTime("");
        setPriority("med");
        setIsRecurring(false);
        setIntervalVal(7);
    };

    const submit = async () => {
        const t = text.trim();
        if (!t || busy) return;
        setBusy(true);
        try {
            const payload = {
                text: t,
                description: description.trim() || null,
                source: "manual",
                type: isRecurring ? "recurring" : (date || time) ? "scheduled" : "todo",
                // Recurring uses `date` as the ANCHOR — if the user didn't pick one, default to
                // today so the cadence has a start point (matches how AI-created recurring tasks
                // are backfilled server-side).
                date: isRecurring ? (date || todayStr()) : (date || null),
                time: time || null,
                interval: isRecurring ? interval : null,
                priority,
            };
            // One idempotency key per logical submit — a retry after a failed request reuses it.
            if (!requestIdRef.current) requestIdRef.current = api.newRequestId();
            const created = await api.createTask({ ...payload, client_request_id: requestIdRef.current });
            requestIdRef.current = null;
            onCreated?.(created);
            toast.success("Task added");
            reset();
            setOpen(false);
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.detail || err?.message || "Add failed");
        } finally {
            setBusy(false);
        }
    };

    const onKeyDown = (e) => {
        // Cmd/Ctrl+Enter submits — matches the muscle memory of chat composers.
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            submit();
        }
    };

    return (
        <div className="relative flex-shrink-0" ref={popRef}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-3 font-brand font-bold uppercase tracking-widest text-xs transition-all ${
                    open
                        ? "border-meye-fuchsia bg-meye-fuchsia/15 text-meye-fuchsia shadow-[0_0_18px_rgba(217,70,239,0.35)]"
                        : "border-meye-cyan/50 bg-meye-card/70 text-meye-cyan hover:border-meye-cyan hover:shadow-[0_0_18px_rgba(34,211,238,0.35)]"
                }`}
                data-testid="manual-task-btn"
                aria-label="Add a task manually"
                title="Add task (bypasses AI)"
            >
                <Plus size={16} strokeWidth={2.5} />
                <span className="hidden sm:inline">task</span>
            </button>

            {open && (
                <div
                    className="absolute right-0 top-[calc(100%+8px)] z-50 w-[min(92vw,380px)] rounded-2xl border border-meye-cyan/40 bg-meye-card/95 backdrop-blur-xl shadow-[0_10px_40px_rgba(0,0,0,0.6)] p-3"
                    data-testid="manual-task-popover"
                >
                    <div className="flex items-center justify-end mb-2">
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="text-zinc-500 hover:text-zinc-100"
                            data-testid="manual-task-close"
                            aria-label="Close"
                        >
                            <X size={14} />
                        </button>
                    </div>

                    <textarea
                        ref={textRef}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={onKeyDown}
                        rows={2}
                        placeholder="what needs doing?"
                        className="w-full bg-black/40 border border-meye-border rounded-md p-2.5 text-sm text-zinc-100 font-mono focus:outline-none focus:border-meye-cyan/60 resize-none"
                        data-testid="manual-task-text"
                    />
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={2}
                        placeholder="body / details (optional)"
                        className="w-full mt-2 bg-black/40 border border-meye-border rounded-md p-2.5 text-xs text-zinc-300 font-mono focus:outline-none focus:border-meye-cyan/60 resize-none"
                        data-testid="manual-task-description"
                    />

                    <div className="flex gap-2 mt-2">
                        <div className="flex-1 min-w-0">
                            <label className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                                <CalendarIcon size={9} /> date
                            </label>
                            <DatePicker value={date} onChange={setDate} testid="manual-task-date" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <label className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                                <ClockIcon size={9} /> time
                            </label>
                            <TimePicker value={time} onChange={setTime} testid="manual-task-time" />
                        </div>
                    </div>

                    {/* Recurring toggle — flip on to convert this from a one-shot task into a
                        cadenced one. The `date` becomes the anchor start, and the chosen interval
                        picker below defines how often it repeats. Matches the TaskEditor semantics. */}
                    <div className="mt-2">
                        <button
                            type="button"
                            onClick={() => setIsRecurring((v) => !v)}
                            className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-brand font-bold px-2.5 py-1 rounded-full border transition-colors ${
                                isRecurring
                                    ? "border-blue-400 text-blue-300 bg-blue-500/10"
                                    : "border-meye-border text-zinc-500 hover:text-zinc-200"
                            }`}
                            data-testid="manual-task-recurring-toggle"
                            aria-pressed={isRecurring}
                        >
                            <RotateCw size={11} strokeWidth={2.5} />
                            {isRecurring ? "recurring · on" : "make recurring"}
                        </button>
                        {isRecurring && (
                            <div className="flex gap-1.5 mt-2" data-testid="manual-task-interval-picker">
                                {[
                                    { l: "daily", v: 1 },
                                    { l: "weekly", v: 7 },
                                    { l: "monthly", v: 30 },
                                    { l: "yearly", v: 365 },
                                ].map((o) => (
                                    <button
                                        key={o.v}
                                        type="button"
                                        onClick={() => setIntervalVal(o.v)}
                                        className={`text-[11px] px-2.5 py-1 rounded border uppercase tracking-widest font-mono transition-colors ${interval === o.v ? "border-meye-fuchsia text-meye-fuchsia bg-meye-fuchsia/10" : "border-meye-border text-zinc-400"}`}
                                        data-testid={`manual-task-interval-${o.v}`}
                                    >
                                        {o.l}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="mt-2">
                        <label className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                            <Flag size={9} /> priority
                        </label>
                        <div className="flex gap-1.5 mt-1">
                            {[
                                { v: "high", l: "high",   iconCls: "text-red-400",      ring: "border-red-400 text-red-300 bg-red-500/10" },
                                { v: "med",  l: "medium", iconCls: "text-meye-cyan",    ring: "border-meye-cyan text-meye-cyan bg-meye-cyan/10" },
                                { v: "low",  l: "low",    iconCls: "text-meye-fuchsia", ring: "border-meye-fuchsia text-meye-fuchsia bg-meye-fuchsia/10" },
                            ].map((o) => (
                                <button
                                    key={o.v}
                                    type="button"
                                    onClick={() => setPriority(o.v)}
                                    className={`text-[11px] px-2.5 py-1 rounded border uppercase tracking-widest font-mono transition-colors inline-flex items-center gap-1 ${priority === o.v ? o.ring : "border-meye-border text-zinc-400 hover:text-zinc-200"}`}
                                    data-testid={`manual-task-priority-${o.v}`}
                                >
                                    <Flag size={10} strokeWidth={2.25} className={priority === o.v ? o.iconCls : "text-zinc-500"} />
                                    {o.l}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center justify-between gap-2 mt-3 pt-2 border-t border-meye-border">
                        <button
                            type="button"
                            onClick={() => {
                                setDate(todayStr());
                            }}
                            className="text-[10px] font-brand uppercase tracking-widest text-zinc-500 hover:text-meye-cyan transition-colors"
                            data-testid="manual-task-today-shortcut"
                        >
                            + today
                        </button>
                        <button
                            type="button"
                            onClick={submit}
                            disabled={!text.trim() || busy}
                            className={`text-xs font-brand font-bold uppercase tracking-widest px-4 py-2 rounded transition-colors flex items-center gap-1.5 ${
                                text.trim() && !busy
                                    ? "bg-meye-cyan hover:bg-cyan-300 text-black"
                                    : "bg-zinc-700 text-zinc-500 cursor-not-allowed"
                            }`}
                            data-testid="manual-task-submit"
                        >
                            <Plus size={13} strokeWidth={2.5} />
                            {busy ? "saving…" : "add task"}
                        </button>
                    </div>
                    <p className="mt-1.5 text-[9px] text-zinc-600 font-mono text-right">
                        ⌘ + enter to save
                    </p>
                </div>
            )}
        </div>
    );
};

export default ManualTaskButton;
